import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Eye, EyeOff } from 'lucide-react';
import { APP_VERSION, APP_VERSION_SHORT } from '../version';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";

function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail) {
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
  }
  if (err?.response?.status === 401) return "Credenciales invalidas";
  if (err?.code === "ERR_NETWORK" || !err?.response) {
    return "Error de conexion con el servidor. Verifica tu red e intenta de nuevo.";
  }
  if (err?.message) return err.message;
  return "Error inesperado";
}

export default function LoginPage() {
  const [mode, setMode] = useState('login'); // 'login' | 'register' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); setIsLoading(true);
    try {
      await register(email, password, name, orgName);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setIsLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(''); setIsLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      const axios = (await import('axios')).default;
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess('Si el email existe, recibiras un enlace para restablecer tu contrasena.');
    } catch (err) {
      setError(formatApiError(err));
    } finally { setIsLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b20_1px,transparent_1px),linear-gradient(to_bottom,#1e293b20_1px,transparent_1px)] bg-[size:32px_32px]" />

      <div className="relative z-10 w-full max-w-md px-4 sm:px-0">
        <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
          <img src={LOGO_URL} alt="ParkingVision AI" className="h-10 sm:h-14 w-auto" />
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">ParkingVision AI</h1>
            <p className="text-xs sm:text-sm text-slate-400">by Visual Ruptive</p>
            <span
              className="inline-block mt-1 text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800/60 text-emerald-300 border border-emerald-500/30"
              title={APP_VERSION}
              data-testid="app-version-login"
            >
              {APP_VERSION_SHORT}
            </span>
          </div>
        </div>

        <Card className="bg-slate-800 border-slate-700 shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl font-bold text-white">
              {mode === 'login' ? 'Iniciar Sesion' : mode === 'register' ? 'Crear Cuenta' : 'Recuperar Contrasena'}
            </CardTitle>
            <CardDescription className="text-slate-400">
              {mode === 'login' ? 'Ingresa tus credenciales' : mode === 'register' ? 'Registra tu organizacion' : 'Ingresa tu email para recibir un enlace'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-4">
                {error && <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span></div>}
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@ejemplo.com"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="login-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Contrasena</Label>
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="********"
                      className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 pr-10" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 font-semibold" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? 'Ingresando...' : 'Ingresar'}
                </Button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={() => { setMode('forgot'); setError(''); }} className="text-blue-400 hover:text-blue-300" data-testid="forgot-password-link">Olvide mi contrasena</button>
                  <button type="button" onClick={() => { setMode('register'); setError(''); }} className="text-blue-400 hover:text-blue-300" data-testid="register-link">Crear cuenta</button>
                </div>
              </form>
            )}

            {mode === 'register' && (
              <form onSubmit={handleRegister} className="space-y-4">
                {error && <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span></div>}
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Tu nombre"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Nombre de Organizacion</Label>
                  <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} placeholder="Mi Estacionamiento"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-org-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@ejemplo.com"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Contrasena</Label>
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Min 6 caracteres"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="register-password-input" />
                </div>
                <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11 font-semibold" disabled={isLoading} data-testid="register-submit-btn">
                  {isLoading ? 'Creando...' : 'Crear Cuenta'}
                </Button>
                <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-blue-400 hover:text-blue-300 text-sm w-full text-center">Ya tengo cuenta</button>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgot} className="space-y-4">
                {error && <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-sm"><AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span></div>}
                {success && <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400 text-sm">{success}</div>}
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="email@ejemplo.com"
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11 font-semibold" disabled={isLoading} data-testid="forgot-submit-btn">
                  {isLoading ? 'Enviando...' : 'Enviar Enlace'}
                </Button>
                <button type="button" onClick={() => { setMode('login'); setError(''); setSuccess(''); }} className="text-blue-400 hover:text-blue-300 text-sm w-full text-center">Volver al login</button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-slate-600 text-xs mt-6">
          Desarrollado por <a href="https://www.disruptive-sw.com" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white underline">Disruptive Software</a>. Todos los derechos reservados. | <a href="mailto:info@disruptive-sw.com" className="text-slate-400 hover:text-white">info@disruptive-sw.com</a>
        </p>
        <p className="text-center text-slate-600 text-[10px] mt-2 font-mono" data-testid="app-version-login-footer">
          {APP_VERSION}
        </p>
      </div>
    </div>
  );
}
